import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

// ---------- společné helpery ----------

/**
 * `timestamptz` z DB <-> hodnota `<input type="datetime-local">`. Čas slotu je „nástěnný" údaj
 * (kdy se sejít na dráze), ne okamžik v čase, takže se posílá i čte v UTC a nikdy se neposouvá
 * podle časové zóny prohlížeče — jinak by admin v Praze zadal 9:00 a příště viděl 10:00.
 */
fun isoToLocalInput(iso: String): String = iso.take(16)

fun localInputToIso(value: String): String = if (value.length == 16) "$value:00+00:00" else value

fun formatDateTime(iso: String): String {
    val v = iso.take(16)
    val date = v.substringBefore("T")
    val time = v.substringAfter("T", "")
    return if (time.isBlank()) formatDate(date) else "${formatDate(date)} $time"
}

/** Zobrazí časový rozsah stručně; při stejném dni datum neopakuje. */
fun formatSlotDateTimeRange(startsAt: String, endsAt: String?): String {
    if (endsAt.isNullOrBlank()) return formatDateTime(startsAt)
    val start = startsAt.take(16)
    val end = endsAt.take(16)
    val startDate = start.substringBefore("T")
    val endDate = end.substringBefore("T")
    val startTime = start.substringAfter("T", "")
    val endTime = end.substringAfter("T", "")
    return if (startDate == endDate && startTime.isNotBlank() && endTime.isNotBlank()) {
        "${formatDate(startDate)} $startTime–$endTime"
    } else {
        "${formatDateTime(startsAt)} – ${formatDateTime(endsAt)}"
    }
}

// ---------- Účastníci ----------

/**
 * Účastníci (spec 17.3 a 17.8): ligoví hráči se vybírají ze soupisek sezóny, hosté jsou hráči bez
 * soupisky v téhle sezóně (do žebříčku se pak nedostanou stávající logikou, spec 13.1). Skupiny
 * A/B se přebírají z uloženého rozdělení celé soupisky; hosté se zařazují ručně.
 */
@Composable
fun TournamentEntriesTab(
    token: String?,
    tournament: TournamentDto,
    data: TournamentAdminData,
    canEdit: Boolean,
    onReload: () -> Unit,
) {
    var error by remember(tournament.id) { mutableStateOf<String?>(null) }
    var busy by remember(tournament.id) { mutableStateOf(false) }
    var selected by remember(tournament.id) { mutableStateOf<Set<String>>(emptySet()) }
    var guestPick by remember(tournament.id) { mutableStateOf("") }
    var newGuestName by remember(tournament.id) { mutableStateOf("") }
    var newGuestGender by remember(tournament.id) { mutableStateOf("male") }
    val scope = rememberCoroutineScope()

    val enteredPlayerIds = data.entries.map { it.playerId }.toSet()
    val scoredEntryIds = data.scores.map { it.entryId }.toSet()
    val slotById = data.slots.associateBy { it.id }
    val availableLeaguePlayers = data.rosterPlayers.filter { it.id !in enteredPlayerIds }
    val availableGuests = data.allPlayers.filter { it.id !in enteredPlayerIds && it.id !in data.teamByPlayer }

    suspend fun addEntry(playerId: String) {
        Api.createTournamentEntry(token, tournament.id, playerId)
    }

    Div {
        H3 { Text("Účastníci (${data.entries.size})") }
        error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }

        if (tournament.type == TOURNAMENT_BONUS && data.grouping != null) {
            P(attrs = { classes("muted") }) {
                Text("Skupinu lze změnit ručně i při uloženém rozdělení. Změna se propíše také do rozdělení; uložený průměr zůstane stejný.")
            }
        }

        if (tournament.type == TOURNAMENT_BRACKET) {
            val missingGender = data.entries.filter { it.gender != "male" && it.gender != "female" }
            if (missingGender.isNotEmpty()) {
                P(attrs = { classes("editor-error") }) {
                    Text("Bez vyplněného pohlaví nelze zařadit do kvalifikační tabulky: " +
                        missingGender.joinToString(", ") { it.playerName })
                }
            }
        }

        if (data.entries.isEmpty()) {
            P { Text("Zatím žádní účastníci.") }
        } else {
            Div(attrs = { classes("table-scroll") }) {
                Table {
                    Tr {
                        Th { Text("Hráč") }
                        Th { Text("Tým / host") }
                        Th { Text("Pohlaví") }
                        Th { Text("Termín") }
                        if (tournament.type == TOURNAMENT_BONUS) Th { Text("Skupina") }
                        if (canEdit) Th { }
                    }
                    data.entries.sortedBy { it.playerName.lowercase() }.forEach { entry ->
                        key(entry.id) {
                            Tr {
                                Td { Text(entry.playerName) }
                                Td { Text(teamOrGuestLabel(entry.playerId, data.teamByPlayer)) }
                                Td { Text(genderLabel(entry.gender)) }
                                Td { Text(entry.slotId?.let { slotById[it]?.let { s -> formatSlotDateTimeRange(s.startsAt, s.endsAt) } } ?: "—") }
                                if (tournament.type == TOURNAMENT_BONUS) {
                                    Td {
                                        Select(attrs = {
                                            attr("aria-label", "Skupina hráče ${entry.playerName}")
                                            if (!canEdit || busy) disabled()
                                            onChange { ev ->
                                                val group = ev.value?.ifBlank { null }
                                                busy = true
                                                error = null
                                                scope.launch {
                                                    try {
                                                        Api.setTournamentPlayerGroup(token, tournament.id, entry.playerId, group)
                                                        onReload()
                                                    } catch (e: Throwable) {
                                                        error = "Změna skupiny selhala: ${e.apiMessage()}"
                                                    }
                                                    busy = false
                                                }
                                            }
                                        }) {
                                            if (data.groupPlayers.none { it.playerId == entry.playerId }) {
                                                Option("", attrs = { if (entry.groupCode == null) selected() }) { Text("—") }
                                            }
                                            listOf("A", "B").forEach { g ->
                                                Option(g, attrs = { if (entry.groupCode == g) selected() }) { Text(g) }
                                            }
                                        }
                                    }
                                }
                                if (canEdit) {
                                    Td {
                                        Button(attrs = {
                                            if (busy) disabled()
                                            onClick {
                                                val hasScores = entry.id in scoredEntryIds
                                                val ok = !hasScores || window.confirm(
                                                    "Hráč ${entry.playerName} má zadané výsledky — odebráním se smažou. Pokračovat?"
                                                )
                                                if (ok) {
                                                    busy = true
                                                    scope.launch {
                                                        try {
                                                            Api.deleteTournamentEntry(token, entry.id)
                                                            onReload()
                                                        } catch (e: Throwable) {
                                                            error = "Odebrání selhalo: ${e.apiMessage()}"
                                                        }
                                                        busy = false
                                                    }
                                                }
                                            }
                                        }) { Text("Odebrat") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (!canEdit) return@Div

        // --- přidání ligových hráčů ---
        H4 { Text("Přidat hráče ze soupisek sezóny") }
        if (availableLeaguePlayers.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Všichni hráči ze soupisek už v turnaji jsou.") }
        } else {
            Div(attrs = { classes("checkbox-columns") }) {
                availableLeaguePlayers.forEach { p ->
                    Div {
                        Label {
                            CheckboxInput(checked = p.id in selected) {
                                onInput { ev ->
                                    selected = if (ev.value) selected + p.id else selected - p.id
                                }
                            }
                            Text(" ${p.name} (${data.teamByPlayer[p.id] ?: ""})")
                        }
                    }
                }
            }
            Div(attrs = { classes("save-action") }) {
                Button(attrs = {
                    if (busy || selected.isEmpty()) disabled()
                    onClick {
                        val ids = selected.toList()
                        busy = true
                        error = null
                        scope.launch {
                            try {
                                ids.forEach { addEntry(it) }
                                selected = emptySet()
                                onReload()
                            } catch (e: Throwable) {
                                error = "Přidání selhalo: ${e.apiMessage()}"
                            }
                            busy = false
                        }
                    }
                }) { Text("Přidat vybrané (${selected.size})") }
            }
        }

        // --- hosté ---
        H4 { Text("Přidat hosta") }
        P(attrs = { classes("muted") }) {
            Text("Host = hráč bez soupisky v této sezóně. Jeho hry se do žebříčku jednotlivců nezapočítávají.")
        }
        if (availableGuests.isNotEmpty()) {
            Div {
                Select(attrs = { onChange { guestPick = it.value ?: "" } }) {
                    Option("", attrs = { if (guestPick.isBlank()) selected() }) { Text("— hráč z evidence —") }
                    availableGuests.forEach { p ->
                        Option(p.id, attrs = { if (p.id == guestPick) selected() }) { Text(p.name) }
                    }
                }
                Button(attrs = {
                    if (busy || guestPick.isBlank()) disabled()
                    onClick {
                        val pid = guestPick
                        busy = true
                        scope.launch {
                            try {
                                addEntry(pid)
                                guestPick = ""
                                onReload()
                            } catch (e: Throwable) {
                                error = "Přidání hosta selhalo: ${e.apiMessage()}"
                            }
                            busy = false
                        }
                    }
                }) { Text("Přidat hosta") }
            }
        }
        Div {
            Label { Text("Nový hráč: ") }
            Input(type = InputType.Text) {
                placeholder("jméno")
                value(newGuestName)
                onInput { newGuestName = it.value }
            }
            Select(attrs = { onChange { newGuestGender = it.value ?: "male" } }) {
                Option("male", attrs = { if (newGuestGender == "male") selected() }) { Text("Muž") }
                Option("female", attrs = { if (newGuestGender == "female") selected() }) { Text("Žena") }
            }
            Button(attrs = {
                if (busy || newGuestName.isBlank()) disabled()
                onClick {
                    val name = newGuestName.trim()
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            val created = Api.createPlayer(
                                token, name = name, fullName = null, gender = newGuestGender,
                                registered = false, card = false, email = null, phone = null,
                            )
                            addEntry(created.id)
                            newGuestName = ""
                            onReload()
                        } catch (e: Throwable) {
                            error = "Založení hráče selhalo: ${e.apiMessage()}"
                        }
                        busy = false
                    }
                }
            }) { Text("Založit a přidat") }
        }
    }
}
